import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Badge, Loading, formatDate, niveauMessage, styleMessage } from '../components/Shared';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

const STATUT_COLORS = {
  brouillon:       { bg: 'var(--bg-elevated)',           color: 'var(--text-secondary)',  label: 'Brouillon' },
  validée:         { bg: 'rgba(16,185,129,0.12)',         color: 'var(--accent-emerald)',  label: 'Validée' },
  exportée:        { bg: 'rgba(59,130,246,0.12)',         color: 'var(--accent-blue)',     label: 'Exportée Sage' },
  comptabilisée:   { bg: 'rgba(139,92,246,0.12)',         color: '#a78bfa',               label: 'Comptabilisée' },
};

function StatutBadge({ statut }) {
  const s = STATUT_COLORS[statut] || STATUT_COLORS.brouillon;
  return (
    <span style={{ display:'inline-block', padding:'0.2rem 0.6rem', borderRadius:'10px', fontSize:'0.75rem', fontWeight:700, background:s.bg, color:s.color }}>
      {s.label}
    </span>
  );
}

export default function Prefacturation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, token } = useAuth();
  const [search, setSearch] = useState('');
  const [filtStatut, setFiltStatut] = useState('');
  const [prefactures, setPrefactures] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [msg, setMsg] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [initialSessionId, setInitialSessionId] = useState('');

  // Ouverture automatique du modal si ?session_id= est dans l'URL (bouton Facturer depuis SessionDetail)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get('session_id');
    if (sid) {
      setInitialSessionId(sid);
      setShowNewModal(true);
      // Nettoyer l'URL sans recharger
      navigate('/prefacturations', { replace: true });
    }
  }, []);

  function load() {
    setLoading(true);
    const params = new URLSearchParams({ limit: 100 });
    if (search) params.set('search', search);
    if (filtStatut) params.set('statut', filtStatut);
    fetch(`${API_BASE}/prefactures?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setPrefactures(d.data || []); setTotal(d.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [search, filtStatut]);

  async function handleValider(id) {
    try {
      await fetch(`${API_BASE}/prefactures/${id}/valider`, { method:'POST', headers:{ Authorization:`Bearer ${token}` } });
      load();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  async function handleSupprimer(id, numero) {
    if (!window.confirm(`Supprimer la préfacture ${numero} ?`)) return;
    try {
      const r = await fetch(`${API_BASE}/prefactures/${id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      load();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  async function handleExportSage() {
    const ids = selected.size > 0 ? [...selected].join(',') : '';
    const validees = ids ? prefactures.filter(p => selected.has(p.prefacture_id) && p.statut === 'validée') : prefactures.filter(p => p.statut === 'validée');
    if (!validees.length) return setMsg('❌ Aucune préfacture validée à exporter');
    if (!window.confirm(`Exporter ${validees.length} préfacture(s) vers Sage ? Elles seront marquées "exportée".`)) return;
    setExporting(true); setMsg('');
    try {
      const idsParam = ids ? `?ids=${ids}` : '';
      const r = await fetch(`${API_BASE}/prefactures/export-sage${idsParam}`, { headers:{ Authorization:`Bearer ${token}` } });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `export_sage_${new Date().toISOString().slice(0,10)}.txt`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSelected(new Set()); load();
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setExporting(false); }
  }

  const toggleSelect = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    if (selected.size === prefactures.length) setSelected(new Set());
    else setSelected(new Set(prefactures.map(p => p.prefacture_id)));
  };

  const nbValidees = prefactures.filter(p => p.statut === 'validée').length;
  const totalValidees = prefactures.filter(p => p.statut === 'validée').reduce((s, p) => s + parseFloat(p.montant_ht || 0), 0);

  return (
    <div className="page">
      <PageHeader
        title="Préfacturation"
        subtitle={`${total} document${total > 1 ? 's' : ''}`}
        actions={
          <div style={{ display:'flex', gap:'0.5rem' }}>
            {hasRole('utilisateur') && nbValidees > 0 && (
              <button className="btn-primary" onClick={handleExportSage} disabled={exporting}
                style={{ background:'#1a3668' }}>
                {exporting ? '⏳ Export…' : `⬇ Export Sage (${nbValidees})`}
              </button>
            )}
            {hasRole('utilisateur') && (
              <button className="btn-primary" onClick={() => setShowNewModal(true)}>+ Nouvelle préfacture</button>
            )}
          </div>
        }
      />

      {/* Résumé rapide */}
      {nbValidees > 0 && (
        <div style={{ display:'flex', gap:'1rem', marginBottom:'1rem', flexWrap:'wrap' }}>
          <div style={{ padding:'0.6rem 1rem', background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.25)', borderRadius:'8px', fontSize:'0.85rem' }}>
            <span style={{ color:'var(--text-secondary)' }}>À exporter : </span>
            <strong style={{ color:'var(--accent-emerald)' }}>{nbValidees} préfacture{nbValidees>1?'s':''} — {totalValidees.toLocaleString('fr-FR',{minimumFractionDigits:2})} €</strong>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1rem', flexWrap:'wrap' }}>
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher (n°, session, financeur…)"
          style={{ padding:'0.45rem 0.8rem', borderRadius:'8px', border:'1px solid var(--border)', background:'var(--bg-card)', color:'var(--text-primary)', fontSize:'0.85rem', width:'280px' }}
        />
        <select value={filtStatut} onChange={e => setFiltStatut(e.target.value)}
          style={{ padding:'0.45rem 0.7rem', borderRadius:'8px', border:'1px solid var(--border)', background:'var(--bg-card)', color:'var(--text-primary)', fontSize:'0.85rem' }}>
          <option value="">Tous statuts</option>
          {Object.entries(STATUT_COLORS).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
        </select>
        {(search || filtStatut) && (
          <button onClick={() => { setSearch(''); setFiltStatut(''); }}
            style={{ padding:'0.45rem 0.8rem', borderRadius:'8px', border:'1px solid rgba(239,68,68,0.4)', background:'rgba(239,68,68,0.08)', color:'#f87171', fontSize:'0.82rem', fontWeight:600, cursor:'pointer' }}>
            ✕ Réinitialiser
          </button>
        )}
      </div>

      {msg && <div style={{ marginBottom:'0.75rem', padding:'0.5rem 0.8rem', borderRadius:'6px', fontSize:'0.85rem', ...styleMessage(niveauMessage(msg)) }}>{msg}</div>}

      {loading ? <Loading /> : prefactures.length === 0 ? (
        <div style={{ padding:'3rem', textAlign:'center', color:'var(--text-secondary)', background:'var(--bg-card)', borderRadius:'12px', border:'1px solid var(--border)' }}>
          Aucune préfacture trouvée.
        </div>
      ) : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          {/* En-tête tableau */}
          <div style={{ display:'grid', gridTemplateColumns:'40px 1fr 1.5fr 1.2fr 1fr 100px 120px 100px', gap:'0.5rem', padding:'0.5rem 1rem', fontSize:'0.72rem', fontWeight:700, color:'var(--text-tertiary)', textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:'1px solid var(--border)', background:'var(--bg-elevated)' }}>
            <span><input type="checkbox" onChange={toggleAll} checked={selected.size === prefactures.length && prefactures.length > 0} /></span>
            <span>N° / Date</span>
            <span>Formation / Session</span>
            <span>Client</span>
            <span>Financeur</span>
            <span style={{textAlign:'right'}}>Montant HT</span>
            <span style={{textAlign:'center'}}>Statut</span>
            <span></span>
          </div>
          {prefactures.map(p => (
            <div key={p.prefacture_id}
              style={{ display:'grid', gridTemplateColumns:'40px 1fr 1.5fr 1.2fr 1fr 100px 120px 100px', gap:'0.5rem', padding:'0.65rem 1rem', alignItems:'center', borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background 0.12s' }}
              onClick={() => navigate(`/prefacturations/${p.prefacture_id}`)}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span onClick={e => { e.stopPropagation(); toggleSelect(p.prefacture_id); }}>
                <input type="checkbox" checked={selected.has(p.prefacture_id)} onChange={() => toggleSelect(p.prefacture_id)} onClick={e => e.stopPropagation()} />
              </span>
              <div>
                <div style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--accent-blue)' }}>{p.numero}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>{formatDate(p.created_at)}</div>
              </div>
              <div>
                <div style={{ fontWeight:600, fontSize:'0.85rem' }}>{p.intitule_formation || '—'}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>{p.session_id}</div>
              </div>
              <div style={{ fontSize:'0.83rem', color:'var(--text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {p.client_nom || '—'}
              </div>
              <div style={{ fontSize:'0.83rem', color:'var(--text-secondary)' }}>{p.organisme_nom || p.nom_financeur || '—'}</div>
              <div style={{ textAlign:'right', fontWeight:700, fontSize:'0.88rem' }}>
                {parseFloat(p.montant_ht||0).toLocaleString('fr-FR',{minimumFractionDigits:2})} €
              </div>
              <div style={{ textAlign:'center' }}><StatutBadge statut={p.statut} /></div>
              <div style={{ display:'flex', gap:'0.3rem', justifyContent:'flex-end' }} onClick={e => e.stopPropagation()}>
                {hasRole('utilisateur') && p.statut === 'brouillon' && (
                  <button onClick={() => handleValider(p.prefacture_id)}
                    style={{ padding:'0.2rem 0.5rem', borderRadius:'5px', border:'1px solid rgba(16,185,129,0.4)', background:'transparent', color:'var(--accent-emerald)', fontSize:'0.72rem', cursor:'pointer', whiteSpace:'nowrap' }}>
                    ✓ Valider
                  </button>
                )}
                {hasRole('utilisateur') && p.statut === 'brouillon' && (
                  <button onClick={() => handleSupprimer(p.prefacture_id, p.numero)}
                    style={{ padding:'0.2rem 0.4rem', borderRadius:'5px', border:'1px solid rgba(239,68,68,0.4)', background:'transparent', color:'#f87171', fontSize:'0.72rem', cursor:'pointer' }}>
                    🗑
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal nouvelle préfacture */}
      {showNewModal && <ModalNouvellePrefacture token={token} initialSessionId={initialSessionId} onSave={id => { setShowNewModal(false); setInitialSessionId(''); navigate(`/prefacturations/${id}`); }} onClose={() => { setShowNewModal(false); setInitialSessionId(''); }} />}
    </div>
  );
}

// ─── Modal création préfacture ────────────────────────────────
function ModalNouvellePrefacture({ token, onSave, onClose, initialSessionId = '' }) {
  const [sessions,       setSessions]       = useState([]);
  const [financements,   setFinancements]   = useState([]);
  const [sessionDetail,  setSessionDetail]  = useState(null);  // groupes + inscriptions
  const [sessionDevis,   setSessionDevis]   = useState([]);    // devis liés à la session (repli si pas de convention)
  const [sessionConventions, setSessionConventions] = useState([]); // conventions de la session = SOURCE des montants
  const [form, setForm] = useState({
    session_id: initialSessionId, client_id: '', client_nom: '',
    financement_id: '', periode_debut: '', periode_fin: '',
    heures_realisees: '', montant_ht: '', taux_tva: '0', notes: '',
  });
  const [lignes, setLignes] = useState([{ description:'', quantite:1, unite:'heure', pu_ht:'', montant_ht:'' }]);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  // ── Chargement de la liste des sessions ──────────────────────
  useEffect(() => {
    fetch(`${API_BASE}/sessions?limit=200&actif=true`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r => r.json()).then(d => setSessions(d.data || [])).catch(() => {});
  }, []);

  // ── Quand la session change : pré-remplir dates + charger financements / groupes / devis ──
  useEffect(() => {
    if (!form.session_id) {
      setFinancements([]); setSessionDetail(null); setSessionDevis([]); return;
    }
    // Pré-remplir dates & heures depuis la liste sessions déjà chargée
    if (sessions.length > 0) {
      const sess = sessions.find(s => s.session_id === form.session_id);
      if (sess) {
        setForm(f => ({
          ...f,
          periode_debut:    sess.date_debut ? sess.date_debut.split('T')[0] : f.periode_debut,
          periode_fin:      sess.date_fin   ? sess.date_fin.split('T')[0]   : f.periode_fin,
          heures_realisees: sess.duree != null && sess.duree !== '' ? String(sess.duree) : f.heures_realisees,
          client_id: '', client_nom: '', financement_id: '', montant_ht: '',
        }));
        setLignes([{ description:'', quantite:1, unite:'heure', pu_ht:'', montant_ht:'' }]);
      }
    }
    const h = { Authorization: `Bearer ${token}` };
    // Financements configurés pour cette session
    fetch(`${API_BASE}/sessions/${form.session_id}/financements`, { headers: h })
      .then(r => r.json()).then(d => setFinancements(Array.isArray(d) ? d : [])).catch(() => {});
    // Détail session (groupes + inscriptions)
    fetch(`${API_BASE}/sessions/${form.session_id}`, { headers: h })
      .then(r => r.json()).then(d => setSessionDetail(d)).catch(() => {});
    // Devis liés à la session
    fetch(`${API_BASE}/sessions/${form.session_id}/devis`, { headers: h })
      .then(r => r.json()).then(d => setSessionDevis(d.data || [])).catch(() => {});
    // Conventions de la session : la préfacturation part de la convention (ce que le client a signé)
    fetch(`${API_BASE}/sessions/${form.session_id}/conventions`, { headers: h })
      .then(r => r.json()).then(d => setSessionConventions(d.data || [])).catch(() => setSessionConventions([]));
  }, [form.session_id, sessions]);

  // ── Groupes clients calculés depuis le détail session ────────
  const clientGroups = React.useMemo(() => {
    if (!sessionDetail) return [];
    const { groupes = [], inscriptions = [] } = sessionDetail;
    if (groupes.length > 0) {
      return groupes.map(g => ({
        key:            g.sous_session_id,
        client_id:      g.client_id || null,
        nom_client:     g.nom_client || 'Sans client',
        sous_session_id: g.sous_session_id,
        groupe:         g.groupe,
        total_heures:   g.total_heures,
        stagiaires:     inscriptions
          .filter(i => i.sous_session_id === g.sous_session_id)
          .map(i => ({ stagiaire_id: i.stagiaire_id, nom: i.nom_stagiaire_snapshot })),
      }));
    }
    // Pas de groupes → regrouper par nom_client_snapshot
    if (inscriptions.length > 0) {
      const map = {};
      inscriptions.forEach(i => {
        const key = i.nom_client_snapshot || 'Sans client';
        if (!map[key]) map[key] = { key, client_id: null, nom_client: key, sous_session_id: null, groupe: null, total_heures: null, stagiaires: [] };
        map[key].stagiaires.push({ stagiaire_id: i.stagiaire_id, nom: i.nom_stagiaire_snapshot });
      });
      return Object.values(map);
    }
    return [];
  }, [sessionDetail]);

  // ── Source des montants d'un groupe : la CONVENTION en vigueur ; à défaut (session sans convention), le devis ──
  // → { origine: 'convention'|'devis', reference, devis_id, total_ht, qte, unite, pu_ht, intitule_formation } ou undefined
  function trouverSource(group) {
    const norm = x => (x || '').toLowerCase().trim();
    const conv = sessionConventions.find(c => !c.obsolete && c.prix_calcule &&
      ((group.client_id && c.client_id === group.client_id) || norm(c.client_name) === norm(group.nom_client)));
    if (conv) {
      const px = conv.prix_calcule;
      return {
        origine: 'convention', reference: conv.reference || conv.convention_id, devis_id: conv.devis_id || null,
        total_ht: px.total_ht, qte: px.qte, unite: px.unite, pu_ht: px.pu_ht, intitule_formation: conv.intitule_formation,
      };
    }
    const dv = sessionDevis.find(d =>
      (group.client_id && d.client_id === group.client_id) ||
      norm(d.client_name) === norm(group.nom_client)
    );
    return dv ? { ...dv, origine: 'devis', reference: dv.devis_id } : undefined;
  }

  // ── Sélection d'un groupe client ─────────────────────────────
  function handleSelectClient(group) {
    const dv = trouverSource(group);
    // Financement correspondant : par client_id d'abord, sinon par nom client
    const fin = financements.find(f =>
      (group.client_id && f.client_id === group.client_id) ||
      normNom(f.client_nom) === normNom(group.nom_client)
    );

    const dvTotal = dv ? parseFloat(dv.total_ht || dv.net_ht || 0) : 0;

    // Total pris en charge par les AUTRES financeurs
    const totalAutres = financements
      .filter(f => f !== fin)
      .reduce((s, f) => {
        if (parseFloat(f.montant_ht) > 0) return s + parseFloat(f.montant_ht);
        if (parseFloat(f.pourcentage) > 0 && dvTotal > 0)
          return s + dvTotal * parseFloat(f.pourcentage) / 100;
        return s;
      }, 0);

    // Montant : depuis le financement configuré, sinon reste à charge (devis - autres), sinon total devis
    let montant = '';
    if (fin && parseFloat(fin.montant_ht) > 0) {
      montant = parseFloat(fin.montant_ht).toFixed(2);
    } else if (fin && parseFloat(fin.pourcentage) > 0 && dvTotal > 0) {
      montant = (dvTotal * parseFloat(fin.pourcentage) / 100).toFixed(2);
    } else if (dvTotal > 0) {
      const resteACharge = totalAutres > 0 ? Math.max(0, dvTotal - totalAutres) : dvTotal;
      montant = resteACharge.toFixed(2);
    }
    // Heures : depuis devis si unité heure, sinon depuis total_heures du groupe
    const heures = (dv && parseFloat(dv.qte) > 0 && (dv.unite || '').toLowerCase().includes('heur'))
      ? String(dv.qte)
      : (group.total_heures ? String(group.total_heures) : '');

    // Destinataire : le financeur (OPCO, etc.) si différent du client formé, sinon le client
    const destId  = fin ? (fin.client_id  || group.client_id) : group.client_id;
    const destNom = fin ? (fin.client_nom || group.nom_client) : group.nom_client;

    setForm(f => ({
      ...f,
      client_id:        destId  || '',
      client_nom:       destNom || '',
      financement_id:   fin ? String(fin.financement_id) : '',
      montant_ht:       montant || f.montant_ht,
      heures_realisees: heures  || f.heures_realisees,
    }));

    // Pré-remplir la ligne de détail depuis le devis
    if (dvTotal > 0) {
      const sess = sessions.find(s => s.session_id === form.session_id);
      const uniteDevis = (dv.unite || 'Stagiaire').toLowerCase() === 'stagiaire' ? 'forfait' : (dv.unite || 'heure');
      const ligneTotal = parseFloat(montant || dvTotal);
      const ligneQte   = parseFloat(dv.qte || group.stagiaires.length || 1);
      const lignePuHt  = ligneQte > 0 ? (ligneTotal / ligneQte).toFixed(2) : String(dv.pu_ht || '');
      setLignes([{
        description: dv.intitule_formation || sess?.intitule_formation || 'Formation',
        quantite:    ligneQte,
        unite:       uniteDevis,
        pu_ht:       lignePuHt,
        montant_ht:  ligneTotal.toFixed(2),
      }]);
    }
  }

  // ── Sélection d'un financeur (met à jour destinataire + montant) ──
  function handleSelectFinancement(finId) {
    const fin = financements.find(f => String(f.financement_id) === String(finId));
    if (!fin) { setForm(f => ({ ...f, financement_id: finId })); return; }

    // Calculer le montant pour ce financeur en partant du montant courant
    const baseTotal = parseFloat(form.montant_ht) || 0;
    let newMontant = form.montant_ht;
    if (parseFloat(fin.montant_ht) > 0) {
      newMontant = parseFloat(fin.montant_ht).toFixed(2);
    } else if (parseFloat(fin.pourcentage) > 0 && baseTotal > 0) {
      newMontant = (baseTotal * parseFloat(fin.pourcentage) / 100).toFixed(2);
    }

    setForm(f => ({
      ...f,
      financement_id:   finId,
      client_id:        fin.client_id  || f.client_id,
      client_nom:       fin.client_nom || f.client_nom,
      montant_ht:       newMontant,
    }));

    if (newMontant !== form.montant_ht) {
      setLignes(ls => ls.map((l, i) => {
        if (i !== 0) return l;
        const qte = parseFloat(l.quantite) || 1;
        return { ...l, pu_ht: (parseFloat(newMontant) / qte).toFixed(2), montant_ht: newMontant };
      }));
    }
  }

  // ── Helpers formulaire ────────────────────────────────────────
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function updateLigne(i, k, v) {
    setLignes(ls => {
      const n = [...ls];
      n[i] = { ...n[i], [k]: v };
      if (k === 'quantite' || k === 'pu_ht') {
        const q  = parseFloat(k === 'quantite' ? v : n[i].quantite) || 0;
        const pu = parseFloat(k === 'pu_ht'    ? v : n[i].pu_ht)    || 0;
        n[i].montant_ht = (q * pu).toFixed(2);
      }
      const total = n.reduce((s, l) => s + (parseFloat(l.montant_ht) || 0), 0);
      setForm(f => ({ ...f, montant_ht: total.toFixed(2) }));
      return n;
    });
  }

  async function handleSave() {
    if (!form.session_id) return setError('Session requise');
    if (!form.montant_ht || parseFloat(form.montant_ht) <= 0) return setError('Montant requis');
    setSaving(true); setError('');
    try {
      const body = { ...form, lignes: lignes.filter(l => l.description || parseFloat(l.montant_ht) > 0) };
      const r = await fetch(`${API_BASE}/prefactures`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      onSave(d.prefacture_id);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const inputSt = { width:'100%', padding:'0.45rem 0.7rem', borderRadius:'6px', border:'1px solid var(--border)', background:'var(--bg-card)', color:'var(--text-primary)', fontSize:'0.85rem', boxSizing:'border-box' };
  const labelSt = { display:'block', fontSize:'0.78rem', fontWeight:600, color:'var(--text-secondary)', marginBottom:'0.25rem' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', padding:'2rem', width:'680px', maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}>
        <h3 style={{ margin:'0 0 1.5rem', color:'var(--text-primary)' }}>Nouvelle préfacture</h3>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'1rem' }}>

          {/* Sélecteur de session */}
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelSt}>Session *</label>
            <select value={form.session_id} onChange={e => set('session_id', e.target.value)} style={inputSt}>
              <option value="">— Sélectionner une session —</option>
              {sessions.map(s => (
                <option key={s.session_id} value={s.session_id}>{s.session_id} — {s.intitule_formation}</option>
              ))}
            </select>
          </div>

          {/* Groupes clients (cartes cliquables) */}
          {form.session_id && clientGroups.length > 0 && (
            <div style={{ gridColumn:'1/-1' }}>
              <label style={labelSt}>Facturer pour le client / groupe *</label>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                {clientGroups.map(group => {
                  const norm = s => (s || '').toLowerCase().trim();
                  const isSelected = form.client_nom === group.nom_client && (form.client_id || '') === (group.client_id || '');
                  const dv = trouverSource(group);
                  const dvTotal = dv ? parseFloat(dv.total_ht || dv.net_ht || 0) : 0;

                  // Financement propre à ce groupe
                  const groupFin = financements.find(f =>
                    (group.client_id && f.client_id === group.client_id) ||
                    norm(f.client_nom) === norm(group.nom_client)
                  );
                  // Total pris en charge par les AUTRES financeurs
                  const totalAutres = financements
                    .filter(f => f !== groupFin)
                    .reduce((s, f) => {
                      if (parseFloat(f.montant_ht) > 0) return s + parseFloat(f.montant_ht);
                      if (parseFloat(f.pourcentage) > 0 && dvTotal > 0)
                        return s + dvTotal * parseFloat(f.pourcentage) / 100;
                      return s;
                    }, 0);

                  // Montant affiché sur la carte
                  let displayMontant = dvTotal;
                  let isResteACharge = false;
                  if (groupFin) {
                    if (parseFloat(groupFin.montant_ht) > 0) displayMontant = parseFloat(groupFin.montant_ht);
                    else if (parseFloat(groupFin.pourcentage) > 0 && dvTotal > 0)
                      displayMontant = dvTotal * parseFloat(groupFin.pourcentage) / 100;
                  } else if (totalAutres > 0 && dvTotal > 0) {
                    displayMontant = Math.max(0, dvTotal - totalAutres);
                    isResteACharge = true;
                  }

                  return (
                    <div key={group.key} onClick={() => handleSelectClient(group)} style={{
                      padding:'0.6rem 0.9rem', borderRadius:'8px', cursor:'pointer',
                      border:`1.5px solid ${isSelected ? 'var(--accent-blue)' : 'var(--border)'}`,
                      background: isSelected ? 'rgba(59,130,246,0.07)' : 'var(--bg-elevated)',
                      transition:'border-color 0.15s, background 0.15s',
                    }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'0.5rem' }}>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontWeight:700, fontSize:'0.88rem', color: isSelected ? 'var(--accent-blue)' : 'var(--text-primary)', display:'flex', alignItems:'center', gap:'0.4rem' }}>
                            {group.groupe && (
                              <span style={{ fontSize:'0.7rem', background:'var(--bg-card)', border:'1px solid var(--border)', color:'var(--text-secondary)', padding:'0 5px', borderRadius:'4px', fontWeight:700 }}>
                                Groupe {group.groupe}
                              </span>
                            )}
                            {group.nom_client}
                          </div>
                          {group.stagiaires.length > 0 && (
                            <div style={{ fontSize:'0.75rem', color:'var(--text-tertiary)', marginTop:'0.2rem', lineHeight:1.4 }}>
                              👤 {group.stagiaires.map(s => s.nom).join(' · ')}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          {displayMontant > 0 ? (
                            <>
                              <div style={{ fontSize:'0.85rem', fontWeight:700, color: isResteACharge ? 'var(--accent-orange)' : 'var(--accent-emerald)', whiteSpace:'nowrap' }}>
                                {displayMontant.toLocaleString('fr-FR', { minimumFractionDigits:2 })} € HT
                              </div>
                              {isResteACharge && (
                                <div style={{ fontSize:'0.7rem', color:'var(--accent-orange)', whiteSpace:'nowrap', marginTop:'0.1rem' }}>
                                  Reste à charge
                                </div>
                              )}
                              {groupFin?.pourcentage > 0 && !isResteACharge && (
                                <div style={{ fontSize:'0.7rem', color:'var(--text-tertiary)', whiteSpace:'nowrap', marginTop:'0.1rem' }}>
                                  {groupFin.pourcentage}% du total
                                </div>
                              )}
                            </>
                          ) : dvTotal > 0 ? (
                            <div style={{ fontSize:'0.75rem', color:'var(--text-tertiary)' }}>Couvert</div>
                          ) : (
                            <div style={{ fontSize:'0.75rem', color:'var(--text-tertiary)' }}>Pas de convention ni de devis</div>
                          )}
                          {(group.total_heures > 0) && (
                            <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)', marginTop:'0.1rem' }}>
                              {group.total_heures} h
                            </div>
                          )}
                        </div>
                      </div>
                      {dv && (
                        <div style={{ marginTop:'0.3rem', fontSize:'0.72rem', color:'var(--text-tertiary)', display:'flex', gap:'1rem' }}>
                          {dv.qte > 0 && <span>{dv.qte} {dv.unite || 'stagiaire'}{dv.qte > 1 ? 's' : ''}</span>}
                          {dv.pu_ht > 0 && <span>{parseFloat(dv.pu_ht).toLocaleString('fr-FR',{minimumFractionDigits:2})} €/u</span>}
                          {dv.origine === 'convention'
                            ? <span style={{ color:'var(--accent-blue)', fontWeight:600 }}>Convention {dv.reference}</span>
                            : <span style={{ color:'#f59e0b', fontWeight:600 }}>Devis {dv.reference} (pas de convention)</span>}
                          {totalAutres > 0 && dvTotal > 0 && (
                            <span>Total {dv.origine === 'convention' ? 'convention' : 'devis'} : {dvTotal.toLocaleString('fr-FR',{minimumFractionDigits:2})} €</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Client sélectionné (tag résumé) ou saisie libre si pas de groupes */}
          {form.session_id && clientGroups.length === 0 && (
            <div style={{ gridColumn:'1/-1' }}>
              <label style={labelSt}>Client</label>
              <input value={form.client_nom} onChange={e => set('client_nom', e.target.value)} placeholder="Nom du client (optionnel)" style={inputSt} />
            </div>
          )}

          {/* Financeur */}
          {financements.length > 0 && (
            <div style={{ gridColumn:'1/-1' }}>
              <label style={labelSt}>Financeur</label>
              <select value={form.financement_id} onChange={e => handleSelectFinancement(e.target.value)} style={inputSt}>
                <option value="">— Aucun / Sélectionner —</option>
                {financements.map(f => (
                  <option key={f.financement_id} value={f.financement_id}>
                    {f.client_nom || f.type_financeur || 'Financeur'}
                    {f.pourcentage ? ` (${f.pourcentage}%)` : ''}
                    {parseFloat(f.montant_ht) > 0 ? ` — ${parseFloat(f.montant_ht).toLocaleString('fr-FR',{minimumFractionDigits:2})} €` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Période & heures */}
          <div>
            <label style={labelSt}>Période — début</label>
            <input type="date" value={form.periode_debut} onChange={e => set('periode_debut', e.target.value)} style={inputSt} />
          </div>
          <div>
            <label style={labelSt}>Période — fin</label>
            <input type="date" value={form.periode_fin} onChange={e => set('periode_fin', e.target.value)} style={inputSt} />
          </div>
          <div>
            <label style={labelSt}>Heures réalisées</label>
            <input type="number" min="0" step="0.5" value={form.heures_realisees} onChange={e => set('heures_realisees', e.target.value)} style={inputSt} placeholder="ex : 21" />
          </div>
          <div>
            <label style={labelSt}>TVA (%)</label>
            <input type="number" min="0" step="0.1" value={form.taux_tva} onChange={e => set('taux_tva', e.target.value)} style={inputSt} placeholder="0" />
          </div>
        </div>

        {/* Lignes de détail */}
        <div style={{ marginBottom:'1rem' }}>
          <div style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--text-secondary)', marginBottom:'0.4rem', textTransform:'uppercase', letterSpacing:'0.05em' }}>Lignes de détail</div>
          {lignes.map((l, i) => (
            <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 60px 70px 90px 90px 30px', gap:'0.4rem', marginBottom:'0.35rem', alignItems:'center' }}>
              <input value={l.description} onChange={e => updateLigne(i,'description',e.target.value)} placeholder="Description" style={inputSt} />
              <input type="number" min="0" step="0.5" value={l.quantite} onChange={e => updateLigne(i,'quantite',e.target.value)} style={inputSt} />
              <select value={l.unite} onChange={e => updateLigne(i,'unite',e.target.value)} style={inputSt}>
                <option value="heure">heure</option><option value="forfait">forfait</option><option value="jour">jour</option>
              </select>
              <input type="number" min="0" step="0.01" value={l.pu_ht} onChange={e => updateLigne(i,'pu_ht',e.target.value)} placeholder="PU HT" style={inputSt} />
              <input type="number" value={l.montant_ht} readOnly style={{ ...inputSt, background:'var(--bg-elevated)', color:'var(--text-tertiary)' }} />
              <button onClick={() => { const n=[...lignes]; n.splice(i,1); setLignes(n.length?n:[{description:'',quantite:1,unite:'heure',pu_ht:'',montant_ht:''}]); }}
                style={{ padding:'0.2rem 0.4rem', borderRadius:'4px', border:'none', background:'transparent', color:'#f87171', cursor:'pointer', fontSize:'0.85rem' }}>✕</button>
            </div>
          ))}
          <button onClick={() => setLignes(l => [...l, {description:'',quantite:1,unite:'heure',pu_ht:'',montant_ht:''}])}
            style={{ fontSize:'0.8rem', color:'var(--accent-blue)', background:'transparent', border:'none', cursor:'pointer', padding:'0.2rem 0' }}>
            + Ajouter une ligne
          </button>
        </div>

        {/* Montant total */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'1rem' }}>
          <div>
            <label style={labelSt}>Montant HT total (€) *</label>
            <input type="number" min="0" step="0.01" value={form.montant_ht} onChange={e => set('montant_ht', e.target.value)} style={inputSt} />
          </div>
          <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:'0.45rem' }}>
            {parseFloat(form.taux_tva) > 0 && (
              <span style={{ fontSize:'0.88rem', color:'var(--text-secondary)' }}>
                TTC : <strong>{(parseFloat(form.montant_ht||0) * (1 + parseFloat(form.taux_tva||0)/100)).toLocaleString('fr-FR',{minimumFractionDigits:2})} €</strong>
              </span>
            )}
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelSt}>Notes</label>
            <input value={form.notes} onChange={e => set('notes', e.target.value)} style={inputSt} placeholder="Optionnel" />
          </div>
        </div>

        {error && <div style={{ marginBottom:'0.75rem', padding:'0.5rem 0.8rem', borderRadius:'6px', background:'rgba(239,68,68,0.1)', color:'#f87171', fontSize:'0.85rem' }}>{error}</div>}

        <div style={{ display:'flex', gap:'0.5rem', justifyContent:'flex-end' }}>
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving?'⏳ Création…':'Créer la préfacture'}</button>
        </div>
      </div>
    </div>
  );
}
